import type { SubscriptionView } from '../api/generated/models/SubscriptionView';

/**
 * Whether a subscription row is a pre-created future subscription (the right
 * side of a scheduled downgrade) - explicitly flagged by the backend via
 * `isScheduled`. Such rows are not active yet and must NOT be treated as an
 * active subscription in the UI.
 */
export const isScheduledSubscription = (subscription: SubscriptionView): boolean =>
  subscription.isScheduled === true;

/**
 * Find the pending scheduled change (Subscription B) for the given active
 * subscription A. Uses the explicit `isScheduled` flag from the API and matches
 * the same plugin as A. (Older brittle heuristic on list shape was replaced
 * once the backend exposed `isScheduled`.)
 */
export const findPendingScheduledChange = (
  subscriptions: SubscriptionView[],
  activeSubscription: SubscriptionView,
): SubscriptionView | undefined => {
  if (!activeSubscription.pluginName) return undefined;
  return subscriptions.find((s) =>
    s.id !== activeSubscription.id &&
    isScheduledSubscription(s) &&
    s.isCancelled === false &&
    s.pluginName === activeSubscription.pluginName
  );
};

/**
 * Whether subscription A is eligible to invoke change-plan / cancel /
 * cancel-scheduled-change. Mirrors the backend gating from the prompt:
 * `isActive === true && isCancelled === false && paymentType === 'STRIPE'`.
 */
export const canChangePlan = (subscription: SubscriptionView): boolean =>
  subscription.isActive === true &&
  subscription.isCancelled === false &&
  subscription.paymentType === 'STRIPE';

/**
 * Trial subscription auto-granted by the backend on registration
 * (paymentType 'TRIAL'). Not Stripe-backed: change-plan/cancel are not
 * available; upgrading goes through the regular buy (checkout) flow.
 * The ONLY reliable trial marker is paymentType - the productName is a
 * regular paid product ("Seller2Go Ultimate Monthly").
 */
export const isTrialSubscription = (subscription: SubscriptionView): boolean =>
  subscription.paymentType === 'TRIAL';
